import React, { useState, useEffect } from 'react';
import bedrockService from '../services/bedrockService';
import outlookService from '../services/outlookService';
import sharepointService from '../services/sharepointService';

const REDACTION_CATEGORY_LABELS = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name',
  personal_address: 'Address',
  birthday: 'Birthday',
  full_ssn: 'Full SSN',
  partial_ssn: 'Partial SSN',
  full_account_number: 'Full Account Number',
  partial_account_number: 'Partial Account Number',
  itin: 'ITIN',
  ptin: 'PTIN',
  ein: 'EIN',
  personal_phone: 'Phone',
  personal_email: 'Email',
};

// Derive the client-news target environment from the current origin:
// tools-qa.* => 'qa', tools.* => 'prod', anything else (local/dev) => 'qa'.
const deriveClientNewsEnv = () => {
  const host = (typeof window !== 'undefined' && window.location.hostname) || '';
  if (host.includes('tools-qa')) return 'qa';
  if (host.includes('tools')) return 'prod';
  return 'qa';
};

const Settings = () => {
  // Doc Types state (with embedded sub_types)
  const [docTypesList, setDocTypesList] = useState([]);
  const [docTypeBusy, setDocTypeBusy] = useState(false);
  const [editingDocTypeId, setEditingDocTypeId] = useState(null); // null, '__new__', or the ID being edited
  const [docTypeForm, setDocTypeForm] = useState({ name: '', short_description: '', sub_types: [] });
  const [subTypeInput, setSubTypeInput] = useState({ name: '', short_description: '' }); // For adding new sub_types

  // Loading state
  const [loading, setLoading] = useState(true);
  const [redactionSettings, setRedactionSettings] = useState({
    enabled: true,
    categories: [],
    availableCategories: [],
  });
  const [redactionBusy, setRedactionBusy] = useState(false);
  const [redactionError, setRedactionError] = useState(null);

  // Client News subscription state
  // Target env is derived from this site's hostname (not user-selectable), which
  // keeps subscribe requests within the same environment and avoids cross-env CORS blocks.
  const clientNewsEnv = deriveClientNewsEnv(); // 'qa' or 'prod'
  const [clientNewsBusy, setClientNewsBusy] = useState(false);
  const [clientNewsDeleteBusy, setClientNewsDeleteBusy] = useState(false);
  const [clientNewsResult, setClientNewsResult] = useState(null);
  const [clientNewsError, setClientNewsError] = useState(null);
  // Current (active) clientnews@ subscription, loaded on mount
  const [clientNewsCurrent, setClientNewsCurrent] = useState(null);
  const [clientNewsStatusLoading, setClientNewsStatusLoading] = useState(true);

  // Load doc types on mount
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [docTypesResp, redactionResp] = await Promise.all([
          bedrockService.getDocTypes(),
          sharepointService.getRedactionSettings(),
        ]);
        setDocTypesList(docTypesResp?.doc_types || []);
        setRedactionSettings({
          enabled: !!redactionResp?.enabled,
          categories: redactionResp?.categories || [],
          availableCategories: redactionResp?.availableCategories || [],
        });
        setRedactionError(null);
      } catch (err) {
        console.error('Failed to load settings data:', err);
        setRedactionError('Failed to load SharePoint redaction settings.');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // Refresh doc types
  const refreshDocTypes = async () => {
    try {
      const resp = await bedrockService.getDocTypes();
      setDocTypesList(resp?.doc_types || []);
    } catch (err) {
      console.warn('Failed to refresh doc types', err);
    }
  };

  const handleToggleRedactionCategory = (category) => {
    setRedactionSettings((prev) => {
      const hasCategory = prev.categories.includes(category);
      return {
        ...prev,
        categories: hasCategory
          ? prev.categories.filter((value) => value !== category)
          : [...prev.categories, category],
      };
    });
  };

  const handleSaveRedactionSettings = async () => {
    if (redactionSettings.enabled && redactionSettings.categories.length === 0) {
      alert('Select at least one PII category when SharePoint redaction is enabled.');
      return;
    }

    setRedactionBusy(true);
    setRedactionError(null);
    try {
      const response = await sharepointService.updateRedactionSettings(
        redactionSettings.enabled,
        redactionSettings.categories,
      );
      setRedactionSettings({
        enabled: !!response.enabled,
        categories: response.categories || [],
        availableCategories: response.availableCategories || [],
      });
    } catch (err) {
      console.error('Failed to save SharePoint redaction settings', err);
      setRedactionError(err.response?.data?.detail || err.message || 'Failed to save redaction settings.');
    } finally {
      setRedactionBusy(false);
    }
  };

  // Load the current clientnews@ subscription (which env it points at)
  const refreshClientNewsStatus = async () => {
    setClientNewsStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await outlookService.getWebhookSubscriptions(token);
      const subs = resp?.subscriptions || [];
      const cn = subs.find(s =>
        (s.resource || '').toLowerCase().includes('clientnews@hffinancial.com')
      );
      setClientNewsCurrent(cn || null);
    } catch (err) {
      console.warn('Failed to load client news subscription status', err);
      setClientNewsCurrent(null);
    } finally {
      setClientNewsStatusLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshClientNewsStatus(); }, []);

  // Client News subscription handler
  const handleSubscribeClientNews = async () => {
    if (clientNewsEnv === 'prod' &&
        !window.confirm('Create the Client News subscription against PRODUCTION? This affects live notifications.')) {
      return;
    }
    setClientNewsBusy(true);
    setClientNewsResult(null);
    setClientNewsError(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await outlookService.subscribeClientNews(token, clientNewsEnv);
      setClientNewsResult({ ...resp, _env: clientNewsEnv });
      refreshClientNewsStatus();
    } catch (err) {
      console.error('Failed to subscribe client news', err);
      setClientNewsError(err.message || 'Failed to create subscription');
    } finally {
      setClientNewsBusy(false);
    }
  };

  // Delete the current clientnews@ subscription (isolated from subscribe so the
  // repoint flow is explicit: Delete, then Subscribe against the new env).
  const handleDeleteClientNewsSubscription = async () => {
    if (!clientNewsCurrent?.id) return;
    if (!window.confirm('Delete the current client-news subscription? Notifications stop until you subscribe again.')) {
      return;
    }
    setClientNewsDeleteBusy(true);
    setClientNewsResult(null);
    setClientNewsError(null);
    try {
      const token = localStorage.getItem('token');
      await outlookService.deleteWebhookSubscription(token, clientNewsCurrent.id);
    } catch (err) {
      console.error('Failed to delete client news subscription', err);
      setClientNewsError(err.message || 'Failed to delete subscription');
    } finally {
      setClientNewsDeleteBusy(false);
      refreshClientNewsStatus();
    }
  };

  // Doc Type handlers
  const handleAddDocType = () => {
    setEditingDocTypeId('__new__');
    setDocTypeForm({ name: '', short_description: '', sub_types: [] });
    setSubTypeInput({ name: '', short_description: '' });
  };

  const handleEditDocType = (dt) => {
    setEditingDocTypeId(dt.id);
    setDocTypeForm({
      name: dt.name,
      short_description: dt.short_description || '',
      sub_types: dt.sub_types || []
    });
    setSubTypeInput({ name: '', short_description: '' });
  };

  const handleSaveDocType = async () => {
    const name = (docTypeForm.name || '').trim();
    if (!name) {
      alert('Name is required');
      return;
    }
    setDocTypeBusy(true);
    try {
      if (editingDocTypeId === '__new__') {
        await bedrockService.addDocType(name, docTypeForm.short_description, docTypeForm.sub_types);
      } else {
        await bedrockService.updateDocType(editingDocTypeId, {
          name: name,
          short_description: docTypeForm.short_description,
          sub_types: docTypeForm.sub_types
        });
      }
      await refreshDocTypes();
      setEditingDocTypeId(null);
    } catch (err) {
      console.error('Failed to save doc type', err);
      alert('Failed to save doc type: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDocTypeBusy(false);
    }
  };

  const handleDeleteDocType = async (dt) => {
    if (!dt.id || !window.confirm(`Delete doc type "${dt.name}" and all its sub types? This cannot be undone.`)) return;
    setDocTypeBusy(true);
    try {
      await bedrockService.deleteDocType(dt.id);
      await refreshDocTypes();
    } catch (err) {
      console.error('Failed to delete doc type', err);
      alert('Failed to delete doc type');
    } finally {
      setDocTypeBusy(false);
    }
  };

  // Sub Type handlers (within the form)
  const handleAddSubType = () => {
    const stName = (subTypeInput.name || '').trim();
    if (!stName) return;
    if (docTypeForm.sub_types.some(st => st.name === stName)) {
      alert('This sub type already exists');
      return;
    }
    // Don't include id - backend will generate it on save
    const newSubType = { name: stName, short_description: (subTypeInput.short_description || '').trim() };
    setDocTypeForm({ ...docTypeForm, sub_types: [...docTypeForm.sub_types, newSubType] });
    setSubTypeInput({ name: '', short_description: '' });
  };

  const handleRemoveSubType = (stId, stName) => {
    // Remove by id if available, otherwise by name
    setDocTypeForm({ 
      ...docTypeForm, 
      sub_types: docTypeForm.sub_types.filter(s => stId ? s.id !== stId : s.name !== stName) 
    });
  };

  const handleSubTypeInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSubType();
    }
  };

  // Edit a sub type inline
  const handleEditSubType = (idx, field, value) => {
    const updated = [...docTypeForm.sub_types];
    updated[idx] = { ...updated[idx], [field]: value };
    setDocTypeForm({ ...docTypeForm, sub_types: updated });
  };

  // Helper to get editing doc type name for display
  const getEditingDocTypeName = () => {
    if (editingDocTypeId === '__new__') return null;
    const dt = docTypesList.find(d => d.id === editingDocTypeId);
    return dt?.name || editingDocTypeId;
  };

  // Filter out "Mixed" doc types and sub types - they are virtual/computed types not stored in DDB
  const filteredDocTypesList = docTypesList
    .filter(dt => dt.name !== 'Mixed')
    .map(dt => ({
      ...dt,
      sub_types: (dt.sub_types || []).filter(st => st.name !== 'Mixed')
    }));

  const currentClientNewsEnv = clientNewsCurrent
    ? outlookService.envForNotificationUrl(clientNewsCurrent.notificationUrl)
    : null;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>Settings</h2>
      <p className="text-muted">Manage global lists for file classification and organization.</p>
      
      {/* Doc Types Section */}
      <div className="card mt-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Doc Types</h5>
          <button 
            className="btn btn-sm btn-primary" 
            onClick={handleAddDocType}
            disabled={docTypeBusy || editingDocTypeId}
          >
            <i className="bi bi-plus-lg me-1"></i> Add Doc Type
          </button>
        </div>
        <div className="card-body">
          {/* Info Alert */}
          <div className="alert alert-info small mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Note:</strong> The <em>short description</em> is used by the AI to understand each type when classifying documents. 
            Be descriptive about what kinds of documents belong to each type. 
            <strong> Do not rename a type in a way that changes its meaning</strong> — existing files will not be re-evaluated based on the new meaning.
          </div>
          
          {/* Add/Edit Form */}
          {editingDocTypeId && (
            <div className="card bg-light mb-3">
              <div className="card-body">
                <h6 className="card-title">{editingDocTypeId === '__new__' ? 'Add New Doc Type' : `Edit: ${getEditingDocTypeName()}`}</h6>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label small">Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={docTypeForm.name}
                      onChange={e => setDocTypeForm({ ...docTypeForm, name: e.target.value })}
                      disabled={docTypeBusy}
                    />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small">Short Description</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={docTypeForm.short_description}
                      onChange={e => setDocTypeForm({ ...docTypeForm, short_description: e.target.value })}
                      placeholder="Brief description (optional)"
                      disabled={docTypeBusy}
                    />
                  </div>
                </div>
                
                {/* Sub Types Section */}
                <div className="mt-3">
                  <label className="form-label small">Sub Types</label>
                  <p className="text-muted small mb-2">
                    <i className="bi bi-lightbulb me-1"></i>
                    Sub types provide more specific classification within a doc type. The description helps the AI understand when to use each sub type.
                  </p>
                  <div className="d-flex gap-2 mb-2 align-items-end">
                    <div>
                      <label className="form-label small text-muted mb-0">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={subTypeInput.name}
                        onChange={e => setSubTypeInput({ ...subTypeInput, name: e.target.value })}
                        onKeyDown={handleSubTypeInputKeyDown}
                        placeholder="Sub type name"
                        disabled={docTypeBusy}
                        style={{ width: '180px' }}
                      />
                    </div>
                    <div>
                      <label className="form-label small text-muted mb-0">Description</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={subTypeInput.short_description}
                        onChange={e => setSubTypeInput({ ...subTypeInput, short_description: e.target.value })}
                        onKeyDown={handleSubTypeInputKeyDown}
                        placeholder="Brief description (optional)"
                        disabled={docTypeBusy}
                        style={{ width: '250px' }}
                      />
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-secondary" 
                      onClick={handleAddSubType}
                      disabled={docTypeBusy || !subTypeInput.name.trim()}
                    >
                      <i className="bi bi-plus"></i> Add
                    </button>
                  </div>
                  
                  {/* Sub Types List as Table */}
                  {docTypeForm.sub_types.length === 0 ? (
                    <span className="text-muted small">No sub types defined</span>
                  ) : (
                    <table className="table table-sm table-bordered mb-0" style={{ maxWidth: '600px' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '180px' }}>Name</th>
                          <th>Description</th>
                          <th style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {docTypeForm.sub_types.map((st, idx) => (
                          <tr key={idx}>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm border-0 bg-transparent p-0"
                                value={st.name}
                                onChange={e => handleEditSubType(idx, 'name', e.target.value)}
                                disabled={docTypeBusy}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm border-0 bg-transparent p-0"
                                value={st.short_description || ''}
                                onChange={e => handleEditSubType(idx, 'short_description', e.target.value)}
                                placeholder="..."
                                disabled={docTypeBusy}
                              />
                            </td>
                            <td className="text-center">
                              <button 
                                type="button" 
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={() => handleRemoveSubType(st.id, st.name)}
                                disabled={docTypeBusy}
                                title="Remove"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                
                <div className="mt-3">
                  <button className="btn btn-sm btn-success me-2" onClick={handleSaveDocType} disabled={docTypeBusy}>
                    {docTypeBusy ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg me-1"></i>Save</>}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setEditingDocTypeId(null)} disabled={docTypeBusy}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* List */}
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: '150px' }}>Name</th>
                  <th style={{ width: '200px' }}>Short Description</th>
                  <th>Sub Types</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocTypesList.length === 0 && (
                  <tr><td colSpan="4" className="text-muted text-center">No doc types defined</td></tr>
                )}
                {filteredDocTypesList.map((dt) => (
                  <tr key={dt.id}>
                    <td><strong>{dt.name}</strong></td>
                    <td className="text-muted small">{dt.short_description || '-'}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(!dt.sub_types || dt.sub_types.length === 0) && (
                          <span className="text-muted small">-</span>
                        )}
                        {(dt.sub_types || []).map((st, idx) => (
                          <span 
                            key={idx} 
                            className="badge bg-light text-dark border" 
                            title={st.short_description || ''}
                          >
                            {st.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-link text-primary p-0 me-2" onClick={() => handleEditDocType(dt)} disabled={docTypeBusy || editingDocTypeId} title="Edit">
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteDocType(dt)} disabled={docTypeBusy || editingDocTypeId} title="Delete">
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-muted small mt-2">{filteredDocTypesList.length} doc type{filteredDocTypesList.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">SharePoint Redaction</h5>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSaveRedactionSettings}
            disabled={redactionBusy}
          >
            {redactionBusy ? (
              <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
            ) : (
              <><i className="bi bi-save me-1"></i> Save</>
            )}
          </button>
        </div>
        <div className="card-body">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="sharepointRedactionEnabled"
              checked={redactionSettings.enabled}
              onChange={(e) => {
                setRedactionSettings((prev) => ({
                  ...prev,
                  enabled: e.target.checked,
                }));
              }}
              disabled={redactionBusy}
            />
            <label className="form-check-label" htmlFor="sharepointRedactionEnabled">
              Automatically redact SharePoint uploads during analysis
            </label>
          </div>

          <p className="text-muted small">
            When enabled, SharePoint upload analysis starts a parallel redaction job and approval will only push the redacted version to SharePoint.
          </p>

          <div
            className="mt-2"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: '0.5rem',
            }}
          >
            {(redactionSettings.availableCategories || []).map((category) => (
              <label
                key={category}
                htmlFor={`redaction-category-${category}`}
                className="border rounded px-3 py-2 mb-0 h-100 d-flex align-items-center"
                style={{ cursor: redactionBusy || !redactionSettings.enabled ? 'not-allowed' : 'pointer' }}
              >
                <input
                  className="form-check-input m-0 me-2 flex-shrink-0"
                  type="checkbox"
                  id={`redaction-category-${category}`}
                  checked={redactionSettings.categories.includes(category)}
                  onChange={() => handleToggleRedactionCategory(category)}
                  disabled={redactionBusy || !redactionSettings.enabled}
                />
                <span>
                  {REDACTION_CATEGORY_LABELS[category] || category}
                </span>
              </label>
            ))}
          </div>

          {redactionError && (
            <div className="alert alert-danger small mt-3 mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {redactionError}
            </div>
          )}
        </div>
      </div>

      {/* Client News Notifications Section */}
      <div className="card mt-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Client News Notifications</h5>
          <div className="d-flex gap-2 align-items-center">
            <span
              className={`badge ${clientNewsEnv === 'prod' ? 'bg-danger' : 'bg-info'}`}
              title="Target environment (derived from this site's URL)"
            >
              {clientNewsEnv.toUpperCase()}
            </span>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSubscribeClientNews}
              disabled={clientNewsBusy || clientNewsDeleteBusy}
            >
              {clientNewsBusy ? (
                <><span className="spinner-border spinner-border-sm me-1" />Subscribing…</>
              ) : (
                <><i className="bi bi-bell me-1"></i> Subscribe</>
              )}
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={handleDeleteClientNewsSubscription}
              disabled={clientNewsBusy || clientNewsDeleteBusy || !clientNewsCurrent}
              title={clientNewsCurrent ? 'Delete the current subscription' : 'No active subscription to delete'}
            >
              {clientNewsDeleteBusy ? (
                <><span className="spinner-border spinner-border-sm me-1" />Deleting…</>
              ) : (
                <><i className="bi bi-trash me-1"></i> Delete</>
              )}
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* Current subscription status */}
          <div className="mb-3 pb-2 border-bottom">
            {clientNewsStatusLoading ? (
              <span className="text-muted small">
                <span className="spinner-border spinner-border-sm me-1" />Checking current subscription…
              </span>
            ) : clientNewsCurrent ? (
              <div className="small">
                <span className="me-2">Current subscription routes to</span>
                <span className={`badge ${currentClientNewsEnv === 'qa' ? 'bg-info' : currentClientNewsEnv === 'prod' ? 'bg-danger' : 'bg-secondary'}`}>
                  {currentClientNewsEnv ? currentClientNewsEnv.toUpperCase() : 'UNKNOWN'}
                </span>
                {clientNewsCurrent.expirationDateTime && (
                  <span className="text-muted ms-2">· expires {clientNewsCurrent.expirationDateTime}</span>
                )}
                <div className="text-muted text-truncate mt-1" title={clientNewsCurrent.notificationUrl}>
                  {clientNewsCurrent.notificationUrl}
                </div>
              </div>
            ) : (
              <span className="text-muted small">
                <i className="bi bi-dash-circle me-1"></i>No active client-news subscription.
              </span>
            )}
          </div>

          <p className="text-muted small mb-3">
            Registers the Microsoft Graph change-notification on the shared{' '}
            <strong>clientnews@hffinancial.com</strong> mailbox. New mail there is
            summarized and posted to the Client News Teams channel. This site targets
            the <strong>{clientNewsEnv.toUpperCase()}</strong> environment (from its URL).
            The subscription expires after ~3 days. <strong>Subscribe</strong> is
            idempotent (returns the existing subscription) — use <strong>Delete</strong>
            then Subscribe to refresh, or the other environment's Tools site to repoint there.
          </p>

          {clientNewsResult && (
            <div className="alert alert-success small mb-0">
              <i className="bi bi-check-circle me-2"></i>
              Subscription active{clientNewsResult._env ? ` (${clientNewsResult._env.toUpperCase()})` : ''}.
              <div className="mt-1">
                {clientNewsResult.subscription_id && (
                  <div><strong>ID:</strong> {clientNewsResult.subscription_id}</div>
                )}
                {clientNewsResult.expiration_datetime && (
                  <div><strong>Expires:</strong> {clientNewsResult.expiration_datetime}</div>
                )}
                {clientNewsResult.notification_url && (
                  <div className="text-truncate"><strong>Callback:</strong> {clientNewsResult.notification_url}</div>
                )}
              </div>
            </div>
          )}

          {clientNewsError && (
            <div className="alert alert-danger small mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {clientNewsError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;